// Types and zod schemas for DE configuration (01 §DE configuration, 02 §DE configuration).
// Field names are camelCase versions of the 02 columns. Every object schema is strict: an
// unknown key is an error, never silently dropped.
import { z } from "zod";
import { roleDefaultsSchema } from "./templates/role-defaults";

/** 02 `deploy.path`. */
export const DEPLOY_PATHS = ["done_for_you", "self_serve"] as const;
export const deployPathSchema = z.enum(DEPLOY_PATHS);
export type DeployPath = z.infer<typeof deployPathSchema>;

/**
 * Template class (01 §DE configuration). `managed`: a done-for-you template the platform
 * operator sets up. `basic`: one of the small platform-owned set offered on self-serve; ships
 * with a verification skill. `empty`: the platform's empty template version (standing orders
 * and a role-to-model map only).
 */
export const TEMPLATE_CLASSES = ["managed", "basic", "empty"] as const;
export const templateClassSchema = z.enum(TEMPLATE_CLASSES);
export type TemplateClass = z.infer<typeof templateClassSchema>;

/** Which template classes each deploy path may deploy (02 §DE configuration "Two deploy paths"). */
export const CLASSES_BY_PATH: Readonly<Record<DeployPath, readonly TemplateClass[]>> = {
  done_for_you: ["managed", "basic"],
  self_serve: ["basic", "empty"],
};

/** 02 `agent_template_version.source`. */
export const versionSourceSchema = z.enum(["author", "learning_loop"]);
export type VersionSource = z.infer<typeof versionSourceSchema>;

/** 01 "Model path". `subscription` is the Deployment engineer's seat only (OPEN-QUESTIONS 45). */
export const MODEL_PATHS = ["gateway", "subscription"] as const;
export const modelPathSchema = z.enum(MODEL_PATHS);
export type ModelPath = z.infer<typeof modelPathSchema>;

/** 02 `run.verdict` (null when the run produced no output). */
export const VERDICTS = ["verified", "not_verified", "inconclusive"] as const;
export const verdictSchema = z.enum(VERDICTS);
export type Verdict = z.infer<typeof verdictSchema>;

/** 02 `configuration_bundle.verification_state` / `digital_employee.verification_state`. */
export const verificationStateSchema = z.enum(["verified", "unverified"]);
export type VerificationState = z.infer<typeof verificationStateSchema>;

/** 02 `template_rule.enforcer_kind`, strongest first (01 "Enforcer"). */
export const ENFORCER_KINDS = ["pipeline", "schema", "tool_block", "eval", "written_rule"] as const;
export const enforcerKindSchema = z.enum(ENFORCER_KINDS);
export type EnforcerKind = z.infer<typeof enforcerKindSchema>;
/** Lower is stronger: pipeline change > schema check > tool block > eval > written rule. */
export const ENFORCER_RANK: Readonly<Record<EnforcerKind, number>> = {
  pipeline: 0,
  schema: 1,
  tool_block: 2,
  eval: 3,
  written_rule: 4,
};

/** 02 `template_eval.kind` and `last_result`. */
export const evalKindSchema = z.enum(["fixture", "replay", "live"]);
export const evalResultSchema = z.enum(["pass", "fail", "error"]);

/** 02 `deploy.state`. */
export const DEPLOY_STATES = [
  "staged",
  "approved",
  "handed_off",
  "confirmed",
  "failed",
  "rejected",
] as const;
export const deployStateSchema = z.enum(DEPLOY_STATES);
export type DeployState = z.infer<typeof deployStateSchema>;

const id = z.string().min(1);
/** A single line of text: no line breaks, not blank. */
const line = z
  .string()
  .min(1)
  .refine((s) => !/[\r\n]/.test(s) && s.trim().length > 0, "must be one non-blank line");

/** `{company_id, run_id, run_decision_id?}` — a pointer to a past mistake (02 template_eval/rule). */
export const mistakeRefSchema = z.strictObject({
  companyId: id,
  runId: id,
  runDecisionId: id.optional(),
});
export type MistakeRef = z.infer<typeof mistakeRefSchema>;

/** One line of the always-loaded skill index (01 "Skill index"): a skill and its trigger. */
export const skillIndexEntrySchema = z.strictObject({
  /** Skill key, unique within the index; also the on-demand load key of the leaf skill. */
  skill: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/, "lowercase key: a-z 0-9 . _ -"),
  applyWhen: line,
});
export type SkillIndexEntry = z.infer<typeof skillIndexEntrySchema>;

/** 02 `verification_skill_pack_id`. */
export const verificationSkillRefSchema = z.strictObject({ skillPackId: id });
export type VerificationSkillRef = z.infer<typeof verificationSkillRefSchema>;

/**
 * 02 `template_eval`. `failsOnVersionId`, `passesOnVersionId` and `lastResult` are accepted
 * because they are 02 columns, but validation **ignores** them: only trusted `EvalAttestation`s
 * from an injected runner decide whether an eval passed (template-validate/evals.ts).
 */
export const templateEvalSchema = z.strictObject({
  key: id,
  kind: evalKindSchema,
  specRef: id,
  provesMistakeRef: mistakeRefSchema.optional(),
  failsOnVersionId: id.optional(),
  passesOnVersionId: id.optional(),
  lastResult: evalResultSchema.optional(),
});
export type TemplateEval = z.infer<typeof templateEvalSchema>;

/** One entry of the role-to-model map: a gateway model alias and the path order to reach it. */
export const modelMapEntrySchema = z.strictObject({
  model: id,
  /** Per-run choice (one entry) or a fallback order, e.g. `["subscription", "gateway"]`. */
  paths: z.array(modelPathSchema).min(1).default(["gateway"]),
});
export type ModelMapEntry = z.infer<typeof modelMapEntrySchema>;

const roleKey = z.string().regex(/^[a-z][a-z0-9_]*$/, "role: lowercase snake_case");
/** 01 "Role-to-model map": pipeline role (`draft`, `review`, `verify`, …) → model. */
export const roleModelMapSchema = z.record(roleKey, modelMapEntrySchema);
export type RoleModelMap = z.infer<typeof roleModelMapSchema>;

/** 01 "Standing orders": stored and copied verbatim; must not be blank. */
export const standingOrdersSchema = z
  .string()
  .refine((s) => s.trim().length > 0, "standing orders must not be blank");

/** 02 `template_rule` — one row of the rule → enforcer table. */
export const templateRuleSchema = z.strictObject({
  key: id,
  ruleText: z.string().min(1),
  enforcerKind: enforcerKindSchema,
  enforcerRef: id,
  provenAgainstRef: mistakeRefSchema,
  provenAt: z.string().min(1).optional(),
  reasonIfWritten: z.string().optional(),
});
export type TemplateRule = z.infer<typeof templateRuleSchema>;

/**
 * 02 `agent_template_version`: the unit that is validated and deployed. The template class is
 * not part of the author's payload: it comes from the trusted catalog (`CatalogEntry`), so an
 * author cannot relabel a template `empty` to skip verification.
 */
export const templateVersionSchema = z.strictObject({
  templateId: id,
  versionId: id,
  version: z.int().min(1),
  source: versionSourceSchema,
  supersedesVersionId: id.nullable().default(null),
  skillIndex: z.array(skillIndexEntrySchema),
  verificationSkill: verificationSkillRefSchema.nullable(),
  evals: z.array(templateEvalSchema).default([]),
  roleModelMap: roleModelMapSchema,
  standingOrders: standingOrdersSchema,
  rules: z.array(templateRuleSchema).default([]),
  toolGrants: z.record(z.string(), z.json()).default({}),
  runtimeConfig: z.record(z.string(), z.json()).default({}),
  /**
   * TEMPLATES-T2: a role template's defaults (templates/role-defaults.ts). Absent for every
   * version cut before role templates existed, so their content hash is unchanged.
   */
  roleDefaults: roleDefaultsSchema.optional(),
});
export type TemplateVersion = z.infer<typeof templateVersionSchema>;
export type TemplateVersionInput = z.input<typeof templateVersionSchema>;

/**
 * Trusted catalog facts about a version, supplied by the platform (the global catalog row and
 * its `agent_template.class`), never by the version's author.
 */
export type CatalogEntry = {
  templateClass: TemplateClass;
  /** Content hash of `supersedesVersionId` as recorded in the catalog (learning-loop versions). */
  supersededContentHash?: string;
};

/**
 * 02 `de_template_override`: exactly these two fields may be overridden per company
 * (founder, OPEN-QUESTIONS 43). Strict: any other key is rejected.
 */
export const OVERRIDABLE_FIELDS = ["standingOrders", "roleModelMap"] as const;
export type OverridableField = (typeof OVERRIDABLE_FIELDS)[number];
export const companyOverridesSchema = z.strictObject({
  standingOrders: standingOrdersSchema.optional(),
  roleModelMap: roleModelMapSchema.optional(),
});
export type CompanyOverrides = z.infer<typeof companyOverridesSchema>;

/** 05 §DE configuration "Override verification": pilots only, reason and `pilot_until`. */
export const verificationOverrideSchema = z.strictObject({
  operatorUserId: id,
  reason: z.string().refine((s) => s.trim().length > 0, "reason is required"),
  pilotUntil: z.iso.date(),
});
export type VerificationOverride = z.infer<typeof verificationOverrideSchema>;

/** Seat budget (02 `seat.budget_*`). At least one of money or tokens must be positive. */
export const seatBudgetSchema = z
  .strictObject({
    moneyCents: z.int().min(0).optional(),
    tokens: z.int().min(0).optional(),
    period: z.enum(["day", "week", "month"]),
  })
  .refine((b) => (b.moneyCents ?? 0) > 0 || (b.tokens ?? 0) > 0, "budget must be positive");
export type SeatBudget = z.infer<typeof seatBudgetSchema>;

/** The deploy target: the DE's seat, its named supervisor and budget. */
export const deployTargetSchema = z.strictObject({
  companyId: id,
  deMembershipId: id,
  seatId: id,
  /** `seat.supervisor_seat_id`; must be set. */
  supervisorSeatId: id.nullable(),
  /** The supervisor seat's active assignment is a person (02 invariants). */
  supervisorIsPerson: z.boolean(),
  budget: seatBudgetSchema.nullable(),
  /** The target seat is the Deployment engineer's (the only seat allowed `subscription`). */
  isDeploymentEngineer: z.boolean().default(false),
});
export type DeployTarget = z.infer<typeof deployTargetSchema>;
export type DeployTargetInput = z.input<typeof deployTargetSchema>;
